import { getSizeHeight, getSizeWidth } from "@/utils/helper";

export interface DesignerToolConfigInput {
  size: string;
  side: string;
  type: string;
  content?: string;
  /**
   * The tool's root element sizes itself to `100vh` unless this is set, in which
   * case it fills its parent (`height: 100%`) instead — the mechanism the tool
   * itself exposes for embedding it at less than full viewport height. Since the
   * tool's config store replaces `appearance` wholesale rather than merging it
   * field by field, this pulls in the tool's own default palette/rounding/fonts
   * alongside the flag so nothing else in `appearance` regresses.
   */
  isWrappedInModal?: boolean;
  /** Marks this instance of the tool as a preview (e.g. the landing page demo). */
  is_preview?: boolean;
  isTopRightExitButtonVisible?: boolean;
  isBottomLeftExitButtonVisible?: boolean;
}

// Mirrors designer-tool-package's default `appearance` (src/config.ts) so it can be
// spread alongside `isWrappedInModal: true` without losing the rest of the theme.
const DEFAULT_APPEARANCE = {
  colors: {
    "wbg-dt-primary-25": "#FCFAFF",
    "wbg-dt-primary-50": "#F9F5FF",
    "wbg-dt-primary-100": "#F4EBFF",
    "wbg-dt-primary-200": "#E9D7FE",
    "wbg-dt-primary-300": "#D6BBFB",
    "wbg-dt-primary-400": "#B692F6",
    "wbg-dt-primary-500": "#9E77ED",
    "wbg-dt-primary-600": "#7F56D9",
    "wbg-dt-primary-700": "#6941C6",
    "wbg-dt-primary-800": "#53389E",
    "wbg-dt-primary-900": "#42307D",
    "wbg-dt-primary-950": "#2C1C5F",

    "wbg-dt-secondary-25": "#FCFCFD",
    "wbg-dt-secondary-50": "#F9FAFB",
    "wbg-dt-secondary-100": "#F2F4F7",
    "wbg-dt-secondary-200": "#EAECF0",
    "wbg-dt-secondary-300": "#D0D5DD",
    "wbg-dt-secondary-400": "#98A2B3",
    "wbg-dt-secondary-500": "#667085",
    "wbg-dt-secondary-600": "#475467",
    "wbg-dt-secondary-700": "#344054",
    "wbg-dt-secondary-800": "#182230",
    "wbg-dt-secondary-900": "#101828",
    "wbg-dt-secondary-950": "#0C111D",

    "wbg-dt-error-25": "#FFFBFA",
    "wbg-dt-error-50": "#FEF3F2",
    "wbg-dt-error-100": "#FEE4E2",
    "wbg-dt-error-200": "#FECDCA",
    "wbg-dt-error-300": "#FDA29B",
    "wbg-dt-error-400": "#F97066",
    "wbg-dt-error-500": "#F04438",
    "wbg-dt-error-600": "#D92D20",
    "wbg-dt-error-700": "#B42318",
    "wbg-dt-error-800": "#912018",
    "wbg-dt-error-900": "#7A271A",
    "wbg-dt-error-950": "#55160C",

    "wbg-dt-warning-25": "#FFFCF5",
    "wbg-dt-warning-50": "#FFFAEB",
    "wbg-dt-warning-100": "#FEF0C7",
    "wbg-dt-warning-200": "#FEDF89",
    "wbg-dt-warning-300": "#FEC84B",
    "wbg-dt-warning-400": "#FDB022",
    "wbg-dt-warning-500": "#F79009",
    "wbg-dt-warning-600": "#DC6803",
    "wbg-dt-warning-700": "#B54708",
    "wbg-dt-warning-800": "#93370D",
    "wbg-dt-warning-900": "#7A2E0E",
    "wbg-dt-warning-950": "#4E1D09",

    "wbg-dt-success-25": "#F6FEF9",
    "wbg-dt-success-50": "#ECFDF3",
    "wbg-dt-success-100": "#DCFAE6",
    "wbg-dt-success-200": "#A9EFC5",
    "wbg-dt-success-300": "#75E0A7",
    "wbg-dt-success-400": "#47CD89",
    "wbg-dt-success-500": "#17B26A",
    "wbg-dt-success-600": "#079455",
    "wbg-dt-success-700": "#067647",
    "wbg-dt-success-800": "#085D3A",
    "wbg-dt-success-900": "#074D31",
    "wbg-dt-success-950": "#053321",

    "wbg-dt-light": "#EBEBEB",
    "wbg-dt-dark": "#212121",
    "wbg-dt-off-white": "#ffffff40",
    "wbg-dt-light-blue": "#003C7C",

    "wbg-dt-primary-ring": "#9E77ED3D",
    "wbg-dt-secondary-ring": "#98A2B324",
    "wbg-dt-error-ring": "#F044383D",

    "sb-color-25": "#FCFCFD",
    "sb-color-50": "#F4F6FF",
    "sb-color-100": "#DDE1FF",
    "sb-color-200": "#C5CCFF",
    "sb-color-300": "#ADB7FF",
    "sb-color-400": "#919EF7",
    "sb-color-500": "#6B78D5",
    "sb-color-600": "#4A57B3",
    "sb-color-700": "#303C91",
    "sb-color-800": "#1B256F",
    "sb-color-900": "#0C144D",
    "sb-color-950": "#0C111D",

    "tooltip-bg-color": "#4A57B3",
    "tooltip-text-color": "#ffffff",
  },
  rounding: {
    "wbg-dt-sm": "0.125rem",
    "wbg-dt-def": "0.25rem",
    "wbg-dt-md": "0.375rem",
    "wbg-dt-lg": "0.5rem",
    "wbg-dt-xl": "0.625rem",
    "wbg-dt-2xl": "0.75rem",
    "wbg-dt-full": "9999px",
    "wbg-dt-sidebar": "0px",
  },
  fonts: {
    "wbg-dt-sans": "Inter",
    "wbg-dt-serif": "ui-serif",
    "wbg-dt-header": "PT Sans",
  },
  isBackButtonVisible: false,
};

export function buildDesignerToolConfig({
  size,
  side,
  type,
  content,
  isWrappedInModal,
  is_preview,
  isTopRightExitButtonVisible,
  isBottomLeftExitButtonVisible,
}: DesignerToolConfigInput) {
  const width = getSizeWidth(size);
  const height = getSizeHeight(size);

  return {
    size: { w: width, h: height },
    flags: {
      allow_locked_item_editing: true,
      is_preview: is_preview ?? false,
    },
    sideSelector: {
      front: side === "front",
      back: side === "back",
    },
    address_box: {
      visible: true,
      width: width === 6 && height === 4 ? 3.2835 : 4,
      height: 2.375,
      isLetter: type === "letter",
    },
    htmlButtonBehaviour: "save",
    isTopRightExitButtonVisible: isTopRightExitButtonVisible ?? true,
    isBottomLeftExitButtonVisible: isBottomLeftExitButtonVisible ?? true,
    initial_html: content || undefined,
    api: { base_url: `${window.location.origin}/api` },
    ...(isWrappedInModal ? { appearance: { ...DEFAULT_APPEARANCE, isWrappedInModal: true } } : {}),
  };
}
